import { Autocomplete, Avatar, AvatarGroup, Box, Chip, CircularProgress, TextField, Tooltip, useTheme } from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import { FC, useMemo } from 'react'
import { EntityAvatar } from 'theme/components'
import { User } from 'types'

/** Maximum number of chips to show before collapsing to avatar group */
const MAX_VISIBLE_CHIPS = 2

interface TeamRoleAutocompleteProps {
  /** The role category (AM, PM, DMS, SEO, MIA, BDE, QA, other) */
  role: string
  /** Current selected user UUIDs for this role */
  value: string[]
  /** Callback when selection changes */
  onChange: (fieldName: string, newValue: string[]) => void
  /** Field name for the onChange callback */
  fieldName: string
  /** Whether the field has unsaved changes */
  isDirty?: boolean
  /** Fields currently being saved */
  savingFields?: string[]
  /** Label for the autocomplete */
  label?: string
}

const TeamRoleAutocomplete: FC<TeamRoleAutocompleteProps> = ({
  role,
  value = [],
  onChange,
  fieldName,
  isDirty = false,
  savingFields = [],
  label,
}) => {
  const theme = useTheme()
  const { data: users, isLoading } = useUserItems()

  // Show all users for every role category
  const filteredUsers = useMemo(() => {
    if (!users) return []
    return users
  }, [users])

  // Get selected users from value (array of user_uuids)
  const selectedUsers = useMemo(() => {
    if (!users?.length || !value?.length) return []
    // Handle case where value might already be user objects (from initial load)
    return value
      .map(item => {
        // If item is already a user object, use it directly
        if (typeof item === 'object' && item !== null && 'user_uuid' in (item as object)) {
          return item as User
        }
        // Otherwise look up by UUID
        const uuid = typeof item === 'string' ? item : (item as any)?.user_uuid
        return users.find(u => u.user_uuid === uuid)
      })
      .filter((u): u is User => !!u)
  }, [users, value])

  const handleChange = (_: any, newValue: User[]) => {
    const userIds = newValue.map(user => user.user_uuid)
    onChange(fieldName, userIds)
  }

  if (isLoading) {
    return <CircularProgress size={24} />
  }

  return (
    <Autocomplete
      multiple
      options={filteredUsers}
      value={selectedUsers}
      onChange={handleChange}
      getOptionLabel={(option: User | string) => {
        // Handle case where option might be a string (UUID) during initial render
        if (typeof option === 'string') return ''
        return option?.name || `${option?.firstName || ''} ${option?.lastName || ''}`.trim() || option?.email || ''
      }}
      isOptionEqualToValue={(option, val) => {
        const optionId = typeof option === 'string' ? option : option?.user_uuid
        const valId = typeof val === 'string' ? val : val?.user_uuid
        return optionId === valId
      }}
      renderOption={(props, option: User) => {
        const userName = option?.name || `${option?.firstName || ''} ${option?.lastName || ''}`.trim() || 'Unknown'
        return (
          <Box component="li" {...props} key={option?.user_uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EntityAvatar
              name={userName}
              photoUrl={option?.image_512}
              size="sm"
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <span>{userName}</span>
              {option?.role && (
                <span style={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                  {option.role}
                </span>
              )}
            </Box>
          </Box>
        )
      }}
      renderTags={(tagValue, getTagProps) => {
        // Cast to User[] since we only store User objects (not strings)
        const users = tagValue as User[]

        // If more than MAX_VISIBLE_CHIPS, show compact avatar group
        if (users.length > MAX_VISIBLE_CHIPS) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
              <AvatarGroup
                max={4}
                spacing="small"
                sx={{
                  '& .MuiAvatar-root': {
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    border: `2px solid ${theme.palette.background.input}`,
                  },
                }}
              >
                {users.map(option => {
                  const userName =
                    option?.name ||
                    `${option?.firstName || ''} ${option?.lastName || ''}`.trim() ||
                    'Unknown'
                  return (
                    <Tooltip key={option?.user_uuid} title={userName} arrow>
                      <Avatar alt={userName} src={option?.image_512}>
                        {userName.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  )
                })}
              </AvatarGroup>
              {/* Delete button for clearing all - shown on hover */}
              <Chip
                size="small"
                label={`${users.length} selected`}
                onDelete={() => onChange(fieldName, [])}
                sx={{
                  height: 22,
                  '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                  '& .MuiChip-deleteIcon': { fontSize: '1rem' },
                }}
              />
            </Box>
          )
        }

        // Default: show individual chips with avatars
        return users.map((option, index) => {
          const userName =
            option?.name || `${option?.firstName || ''} ${option?.lastName || ''}`.trim() || 'Unknown'
          return (
            <Chip
              {...getTagProps({ index })}
              key={option?.user_uuid || index}
              avatar={<EntityAvatar name={userName} photoUrl={option?.image_512} size="xs" />}
              label={userName}
              size="small"
            />
          )
        })
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label || role}
          placeholder={selectedUsers.length === 0 ? `Select ${role}...` : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {savingFields.includes(fieldName) && <CircularProgress size={16} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          bgcolor: isDirty ? theme.palette.text.dirty : theme.palette.background.input,
        },
      }}
    />
  )
}

export default TeamRoleAutocomplete
